import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../hooks/useTier';

// Import org dashboard components
import {
  OrgTopBar,
  OrgSidebar,
  OrgOverview,
  OrgMembers,
  OrgMeetings,
  OrgAnalytics,
  OrgRecordings,
  OrgNotes,
  OrgBilling,
  OrgSettings,
  OrgInviteMembers
} from '../components/org-dashboard';

/**
 * Organization Dashboard - Team Tier Control Room
 *
 * Features:
 * - Sidebar navigation (Overview, Members, Meetings, Analytics, etc.)
 * - Persistent top bar with org info
 * - Role-based access control
 * - Real-time data updates
 *
 * Route: /org/:orgSlug
 */

export default function OrganizationDashboard() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const { userProfile, user } = useAuth();
  const { isTeam } = useTier();

  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [growth, setGrowth] = useState({ meetings: 0, hours: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Check if user has access to this org
  useEffect(() => {
    if (!isTeam || !userProfile?.organizationId) {
      navigate('/dashboard');
      return;
    }

    loadOrganization();
  }, [orgSlug, userProfile]);

  const loadOrganization = async () => {
    try {
      // Find organization by slug
      const orgsRef = collection(db, 'organizations');
      const q = query(orgsRef, where('slug', '==', orgSlug));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        alert('Organization not found');
        navigate('/dashboard');
        return;
      }

      const orgDoc = snapshot.docs[0];
      const orgData = { id: orgDoc.id, ...orgDoc.data() };

      // Try to verify user is a member, but don't fail if member doc doesn't exist yet
      let userRole = 'member'; // default role
      try {
        const memberDoc = await getDoc(doc(db, `organizations/${orgDoc.id}/members/${user.uid}`));
        if (memberDoc.exists()) {
          userRole = memberDoc.data().role || 'member';
        }
      } catch (memberError) {
        console.warn('Could not verify member role:', memberError);
      }

      setOrganization({
        ...orgData,
        userRole: userRole
      });

      // Load members with real-time updates
      try {
        const membersQuery = query(collection(db, `organizations/${orgDoc.id}/members`));
        const unsubscribe = onSnapshot(
          membersQuery,
          (snapshot) => {
            const membersData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              joinedAt: doc.data().joinedAt?.toDate()
            }));
            setMembers(membersData);
          },
          (error) => {
            console.warn('Error loading members:', error);
            setMembers([]);
          }
        );

        // Calculate stats
        calculateStats(orgDoc.id);

        setLoading(false);

        return () => unsubscribe();
      } catch (error) {
        console.warn('Error setting up members listener:', error);
        setMembers([]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading organization:', error);
      setLoading(false);
    }
  };

  const calculateStats = async (orgId) => {
    try {
      // Query real meetings from organization
      const meetingsRef = collection(db, `organizations/${orgId}/meetings`);
      const meetingsSnapshot = await getDocs(meetingsRef);

      let totalMinutes = 0;
      const activities = [];

      // Calculate current month stats
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      let thisMonthMeetings = 0;
      let thisMonthMinutes = 0;
      let lastMonthMeetings = 0;
      let lastMonthMinutes = 0;

      // Calculate meeting stats and growth
      meetingsSnapshot.forEach(doc => {
        const meetingData = doc.data();
        const meetingDate = meetingData.createdAt?.toDate?.() || new Date();

        if (meetingData.duration) {
          totalMinutes += meetingData.duration;
        }

        if (meetingDate >= thisMonthStart) {
          thisMonthMeetings++;
          if (meetingData.duration) thisMonthMinutes += meetingData.duration;
        } else if (meetingDate >= lastMonthStart && meetingDate <= lastMonthEnd) {
          lastMonthMeetings++;
          if (meetingData.duration) lastMonthMinutes += meetingData.duration;
        }

        if (meetingData.createdAt) {
          activities.push({
            type: 'meeting_created',
            user: meetingData.createdByEmail || 'Unknown User',
            action: 'created a meeting',
            timestamp: meetingData.createdAt?.toDate?.() || new Date(),
            icon: '📅',
            color: 'blue'
          });
        }
      });

      // Calculate growth percentages
      const meetingGrowth = lastMonthMeetings > 0
        ? Math.round(((thisMonthMeetings - lastMonthMeetings) / lastMonthMeetings) * 100)
        : (thisMonthMeetings > 0 ? 100 : 0);

      const hoursGrowth = lastMonthMinutes > 0
        ? Math.round(((thisMonthMinutes - lastMonthMinutes) / lastMonthMinutes) * 100)
        : (thisMonthMinutes > 0 ? 100 : 0);

      // Get member join activity
      members.forEach(member => {
        if (member.joinedAt) {
          activities.push({
            type: 'member_joined',
            user: member.email || 'Unknown User',
            action: 'joined the organization',
            timestamp: member.joinedAt,
            icon: '👋',
            color: 'green'
          });
        }
      });

      activities.sort((a, b) => b.timestamp - a.timestamp);

      // Query recordings for storage calculation
      const recordingsRef = collection(db, `organizations/${orgId}/recordings`);
      const recordingsSnapshot = await getDocs(recordingsRef);

      let storageUsed = 0;
      recordingsSnapshot.forEach(doc => {
        const recordingData = doc.data();
        if (recordingData.sizeGB) {
          storageUsed += recordingData.sizeGB;
        } else if (recordingData.duration) {
          storageUsed += (recordingData.duration / 60) * 0.1;
        }
      });

      const statsData = {
        totalMeetings: meetingsSnapshot.size,
        totalMinutes: Math.round(totalMinutes),
        activeUsers: members.filter(m => m.status === 'active').length,
        storageUsed: Math.round(storageUsed * 10) / 10,
        storageLimit: organization?.features?.storageGB || 1000
      };

      setStats(statsData);
      setGrowth({ meetings: meetingGrowth, hours: hoursGrowth });
      setRecentActivity(activities.slice(0, 10));
    } catch (error) {
      console.error('Error calculating stats:', error);
      setStats({
        totalMeetings: 0,
        totalMinutes: 0,
        activeUsers: members.filter(m => m.status === 'active').length,
        storageUsed: 0,
        storageLimit: organization?.features?.storageGB || 1000
      });
      setGrowth({ meetings: 0, hours: 0 });
    }
  };

  const isAdmin = organization?.userRole === 'owner' || organization?.userRole === 'admin';
  const isOwner = organization?.userRole === 'owner';

  // Render current section
  const renderSection = () => {
    const commonProps = { organization, members, stats, isAdmin };

    switch (activeSection) {
      case 'overview':
        return <OrgOverview {...commonProps} growth={growth} recentActivity={recentActivity} />;
      case 'members':
        return <OrgMembers {...commonProps} onInvite={() => setActiveSection('invite')} />;
      case 'meetings':
        return <OrgMeetings {...commonProps} />;
      case 'analytics':
        return <OrgAnalytics {...commonProps} />;
      case 'recordings':
        return <OrgRecordings {...commonProps} />;
      case 'notes':
        return <OrgNotes {...commonProps} />;
      case 'billing':
        return isAdmin ? <OrgBilling {...commonProps} /> : <AccessDenied />;
      case 'settings':
        return isAdmin ? <OrgSettings {...commonProps} isOwner={isOwner} /> : <AccessDenied />;
      case 'invite':
        return isAdmin ? <OrgInviteMembers {...commonProps} /> : <AccessDenied />;
      default:
        return <OrgOverview {...commonProps} growth={growth} recentActivity={recentActivity} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#06c]/30 border-t-[#06c] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/60 text-sm">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Top Bar */}
      <OrgTopBar
        organization={organization}
        members={members}
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      {/* Sidebar */}
      <OrgSidebar
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        isAdmin={isAdmin}
        isMobileOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main Content */}
      <main className="lg:ml-64 min-h-[calc(100vh-4rem)]">
        <div className="p-6 lg:p-8">
          {renderSection()}
        </div>
      </main>
    </div>
  );
}

// Access Denied Component
function AccessDenied() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-center">
        <div className="text-6xl mb-4">🔒</div>
        <h2 className="text-white font-semibold text-xl mb-2">Access Denied</h2>
        <p className="text-white/50 text-sm">You don't have permission to view this section.</p>
      </div>
    </div>
  );
}
